import { useEffect, useState } from 'react'
// The pure, framework-free core lives in a sibling .js module so it can be
// behaviorally unit-tested by node:test with an in-memory DOM shim (no
// jsdom/vitest). See tests/widget-top-layer-portal.test.js. Do NOT inline the
// remove-on-unmount cleanup here — that caused the recurring
// "no popup form was shown" regression.
import { ensureTopLayerPortalIn } from './top-layer-portal-core'

function ensureTopLayerPortal(): HTMLDivElement | null {
  if (typeof document === 'undefined') {
    return null
  }
  return ensureTopLayerPortalIn(document) as HTMLDivElement | null
}

export function useTopLayerPortal() {
  // Resolve synchronously on first render (the widget is client-only) so
  // the portal target is available immediately — no null first frame that
  // could swallow a popup that mounts and reads it on the same tick.
  const [portalTarget, setPortalTarget] = useState<HTMLDivElement | null>(
    () => ensureTopLayerPortal()
  )

  useEffect(() => {
    // Repair on mount in case the root was missing/detached, and adopt the
    // healed reference. We deliberately register NO cleanup: the shared
    // root is a persistent singleton (see top-layer-portal-core.js docs).
    // Deleting it here is exactly what caused the recurring
    // "popup not shown" regression.
    const root = ensureTopLayerPortal()
    if (root && root !== portalTarget) {
      setPortalTarget(root)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return portalTarget
}
